import { USER_TYPE } from 'app/config/users';
import { PAD_VISIT_TYPE } from 'app/config/padVisits';
import {
  VISIT_SCHEDULE_KEYS,
  NORMALIZED_KEYS
} from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/visitScheduleStep.reducer';
import { normalizeVisitPrescriptionSchedule } from 'app/redux/features/pad-initialization/reducer/visit-schedule-step/utils/normalizeVisitPrescriptionSchedule';
import { transformPrescriptionScheduleToLoadSchedule } from './utils/transformPrescriptionScheduleToLoadSchedule.js';
import { shouldIncludeAsCalendarVisitSchedule } from './utils/shouldIncludeAsCalendarVisitSchedule.js';

const isSuspensionVisit = padVisit => {
  const { padVisitType } = padVisit;
  const isSuspension = padVisitType === PAD_VISIT_TYPE.SUSPENSION;

  return isSuspension;
};

const isCooperative = padVisit => {
  const { responsibleId, responsibleType } = padVisit;
  return responsibleType === USER_TYPE.COOPERATIVE || responsibleId;
};

const hasLinkWithPadPrescription = padVisit => {
  const { padPrescriptions = [] } = padVisit;
  return Array.isArray(padPrescriptions) && padPrescriptions.length > 0;
};

export const shouldIncludeAsCalendarVisitScheduleCooperative = padVisit => {
  return (
    isCooperative(padVisit) && !isSuspensionVisit(padVisit) && !hasLinkWithPadPrescription(padVisit)
  );
};

export const shouldIncludeAsSuspensionVisits = padVisit => {
  const { padVisitType } = padVisit;

  const isPadVisitSuspension = padVisitType === PAD_VISIT_TYPE.SUSPENSION;

  return isPadVisitSuspension;
};

/**
 * Creates a new calendar state with filtered entries by removing specified padVisitIds.
 * @param {Object} state - Current state
 * @param {string[]} padVisitIdsToRemove - Array of padVisitIds to remove
 * @returns {Object} New calendar state with filtered entries
 */
// Tornar genérico para qualquer key (mantendo retrocompatibilidade)
const createFilteredScheduleState = (state, padVisitIdsToRemove, scheduleKey) => {
  const scheduleState = state[scheduleKey];

  const byPadVisitId = scheduleState[NORMALIZED_KEYS.BY_PAD_VISIT_ID] || {};
  const padVisitIds = scheduleState[NORMALIZED_KEYS.IDS] || [];

  const filteredByPadVisitId = padVisitIds
    .filter(padVisitId => !padVisitIdsToRemove.includes(padVisitId))
    .reduce((result, padVisitId) => {
      result[padVisitId] = byPadVisitId[padVisitId];
      return result;
    }, {});

  return {
    ...scheduleState,
    [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: filteredByPadVisitId,
    [NORMALIZED_KEYS.IDS]: padVisitIds.filter(
      padVisitId => !padVisitIdsToRemove.includes(padVisitId)
    )
  };
};

/**
 * Handles missing padVisits that need to be added to either calendar or daily schedules.
 * @param {Object} state - Current state
 * @param {Object} newCalendarState - Filtered calendar state
 * @param {Object} newDailyState - Filtered daily state
 * @param {Array} schedulesToAdd - Schedules that need to be added
 * @returns {Object} Object with newCalendarVisitSchedule and newVisitDailySchedule
 */
const handleMissingPadVisits = (
  state,
  newCalendarState,
  newDailyState,
  schedulesToAdd,
  shouldIncludeAsCalendarFn
) => {
  let mergedCalendarIds = [...(newCalendarState[NORMALIZED_KEYS.IDS] || [])];
  let mergedCalendarByPadVisitId = { ...(newCalendarState[NORMALIZED_KEYS.BY_PAD_VISIT_ID] || {}) };

  let mergedDailyIds = [...(newDailyState[NORMALIZED_KEYS.IDS] || [])];
  let mergedDailyByPadVisitId = { ...(newDailyState[NORMALIZED_KEYS.BY_PAD_VISIT_ID] || {}) };

  schedulesToAdd.forEach(schedule => {
    const { padVisit } = schedule;
    const padVisitId = padVisit.id;

    const shouldIncludeAsCalendar = shouldIncludeAsCalendarFn(padVisit);

    if (shouldIncludeAsCalendar) {
      if (!mergedCalendarIds.includes(padVisitId) && !newCalendarState.byPadVisitId[padVisitId]) {
        mergedCalendarIds.push(padVisitId);
        mergedCalendarByPadVisitId[padVisitId] = schedule;
      }
    } else {
      if (!mergedDailyIds.includes(padVisitId) && !newDailyState.byPadVisitId[padVisitId]) {
        mergedDailyIds.push(padVisitId);
        mergedDailyByPadVisitId[padVisitId] = schedule;
      }
    }
  });

  return {
    newCalendarVisitSchedule: {
      ...newCalendarState,
      [NORMALIZED_KEYS.IDS]: mergedCalendarIds,
      [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: mergedCalendarByPadVisitId
    },
    newVisitDailySchedule: {
      ...newDailyState,
      [NORMALIZED_KEYS.IDS]: mergedDailyIds,
      [NORMALIZED_KEYS.BY_PAD_VISIT_ID]: mergedDailyByPadVisitId
    }
  };
};

/**
 * Creates a new event with updated padPrescriptionIds array.
 * @param {Object} event - The event object
 * @param {number} prescriptionId - The prescription ID to add/remove
 * @param {boolean} shouldAdd - true to add, false to remove
 * @returns {Object} New event with updated padPrescriptionIds
 */
const updateEventPadPrescriptionIds = (event, prescriptionId, shouldAdd) => {
  if (!event) return event;

  const currentIds = Array.isArray(event.padPrescriptionIds) ? event.padPrescriptionIds : [];

  let newIds;
  if (shouldAdd) {
    // Add only if not already present
    newIds = currentIds.includes(prescriptionId) ? currentIds : [...currentIds, prescriptionId];
  } else {
    // Remove if present
    newIds = currentIds.filter(id => id !== prescriptionId);
  }

  return {
    ...event,
    padPrescriptionIds: newIds
  };
};

/**
 * Updates all events in a schedule based on padVisitId comparison.
 * @param {Object} schedule - The schedule object
 * @param {number} prescriptionId - The prescription ID
 * @param {number|string|null} sourcePadVisitId - The padVisitId from the prescription
 * @returns {Object} New schedule with updated events
 */
const updateScheduleEvents = (schedule, prescriptionId, sourcePadVisitId) => {
  if (!schedule || !Array.isArray(schedule.events)) {
    return schedule;
  }

  const schedulePadVisitId = schedule.padVisitId;
  const hasMatchingPadVisitId =
    sourcePadVisitId !== null &&
    sourcePadVisitId !== undefined &&
    schedulePadVisitId === sourcePadVisitId;

  const shouldAdd = hasMatchingPadVisitId;

  const updatedEvents = schedule.events.map(event =>
    updateEventPadPrescriptionIds(event, prescriptionId, shouldAdd)
  );

  return {
    ...schedule,
    events: updatedEvents
  };
};

/**
 * Updates padPrescriptionIds in events across all three collections
 * (calendar, daily, prescription) based on the prescriptions array.
 * @param {Object} state - State after schedule movement
 * @param {Array} prescriptionsArray - Array of valid prescriptions with id and padVisitId
 * @returns {Object} State with updated event padPrescriptionIds
 */
const updateAllCollectionsPadPrescriptionIds = (state, prescriptionsArray) => {
  // Helper to update a single collection's events
  const updateCollectionEvents = collection => {
    if (!collection || !collection.byPadVisitId) {
      return collection;
    }

    const updatedByPadVisitId = { ...collection.byPadVisitId };

    Object.keys(updatedByPadVisitId).forEach(padVisitId => {
      const schedule = updatedByPadVisitId[padVisitId];
      let updatedSchedule = { ...schedule };

      // Process each prescription to determine if it should be added/removed
      prescriptionsArray.forEach(prescription => {
        const { id: prescriptionId, padVisitId: sourcePadVisitId } = prescription;

        if (prescriptionId === undefined || prescriptionId === null) {
          return;
        }

        updatedSchedule = updateScheduleEvents(updatedSchedule, prescriptionId, sourcePadVisitId);
      });

      updatedByPadVisitId[padVisitId] = updatedSchedule;
    });

    return {
      ...collection,
      byPadVisitId: updatedByPadVisitId
    };
  };

  return {
    ...state,
    [VISIT_SCHEDULE_KEYS.CALENDAR]: updateCollectionEvents(state.calendarVisitSchedule),
    [VISIT_SCHEDULE_KEYS.DAILY]: updateCollectionEvents(state.calendarDailySchedules),
    [VISIT_SCHEDULE_KEYS.PRESCRIPTION]: updateCollectionEvents(state.visitPrescriptionSchedules)
  };
};

/**
 * Função genérica para intercâmbio entre dois schedules no state redux.
 */
export function exchangeSchedules(
  state,
  prescriptionsArray,
  { calendarKey, prescriptionKey, dailyKey = VISIT_SCHEDULE_KEYS.DAILY }
) {
  if (!Array.isArray(prescriptionsArray) || prescriptionsArray.length === 0) {
    return {
      [calendarKey]: state[calendarKey],
      [dailyKey]: state[dailyKey],
      [prescriptionKey]: {
        ids: [],
        byPadVisitId: {}
      }
    };
  }

  const validPrescriptions = prescriptionsArray.filter(item => item && item.id !== null);

  if (validPrescriptions.length === 0) {
    return {
      [calendarKey]: state[calendarKey],
      [dailyKey]: state[dailyKey],
      [prescriptionKey]: state[prescriptionKey]
    };
  }

  // Consolida schedules existentes das três coleções relevantes
  const allSchedules = [
    ...Object.values(state[calendarKey].byPadVisitId || {}),
    ...Object.values(state[dailyKey].byPadVisitId || {}),
    ...Object.values(state[prescriptionKey].byPadVisitId || {})
  ]
    .filter(Boolean)
    .map(schedule => ({ ...schedule, events: [], prescriptionLoadSchedule: [] }));

  const padVisitIdsToTransfer = prescriptionsArray
    .filter(({ padVisitId, isPADVisitInherited }) => !!padVisitId && !isPADVisitInherited)
    .map(({ padVisitId }) => padVisitId);

  const schedulesForPrescription = allSchedules
    .filter(schedule => padVisitIdsToTransfer.includes(schedule.padVisitId))
    .map(schedule => ({
      ...schedule,
      prescriptionLoadSchedule: transformPrescriptionScheduleToLoadSchedule(
        prescriptionsArray,
        schedule.padVisit
      )
    }));

  const schedulesForOriginalCollections = allSchedules.filter(
    schedule => !padVisitIdsToTransfer.includes(schedule.padVisitId)
  );

  const newVisitPrescriptionSchedule = normalizeVisitPrescriptionSchedule(schedulesForPrescription);

  // Remove dos schedules originais os padVisitId que migraram
  const filteredCalendar = createFilteredScheduleState(state, padVisitIdsToTransfer, calendarKey);
  const filteredDaily = createFilteredScheduleState(state, padVisitIdsToTransfer, dailyKey);

  const isCooperative = calendarKey === VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE;
  const shouldIncludeFn = isCooperative
    ? shouldIncludeAsCalendarVisitScheduleCooperative
    : shouldIncludeAsCalendarVisitSchedule;

  const { newCalendarVisitSchedule, newVisitDailySchedule } = handleMissingPadVisits(
    state,
    filteredCalendar,
    filteredDaily,
    schedulesForOriginalCollections,
    shouldIncludeFn
  );

  const intermediateState = {
    ...state,
    [calendarKey]: newCalendarVisitSchedule,
    [dailyKey]: newVisitDailySchedule,
    [prescriptionKey]: newVisitPrescriptionSchedule
  };

  // Utilitário já atende as três coleções (calendarKey, dailyKey, prescriptionKey)
  return updateAllCollectionsPadPrescriptionIds(intermediateState, validPrescriptions, {
    calendarKey,
    dailyKey,
    prescriptionKey
  });
}

export const computeUpdateSchedulePrescriptionState = (state, prescriptionsArray) =>
  exchangeSchedules(state, prescriptionsArray, {
    calendarKey: VISIT_SCHEDULE_KEYS.CALENDAR,
    prescriptionKey: VISIT_SCHEDULE_KEYS.PRESCRIPTION,
    dailyKey: VISIT_SCHEDULE_KEYS.DAILY
  });

export const computeUpdateSchedulePrescriptionCooperativeState = (state, prescriptionsArray) =>
  exchangeSchedules(state, prescriptionsArray, {
    calendarKey: VISIT_SCHEDULE_KEYS.CALENDAR_COOPERATIVE,
    prescriptionKey: VISIT_SCHEDULE_KEYS.PRESCRIPTION_COOPERATIVE,
    dailyKey: VISIT_SCHEDULE_KEYS.DAILY_COOPERATIVE
  });
